import api from '@/libs/axiosInstance';
import type { ApiResponse, ProgramStatus } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

interface ProgramsCountFilters {
  status?: ProgramStatus;
}

interface ProgramsCountResponse {
  count: number;
}

/**
 * Lightweight program count for the browse status tabs. Dedicated endpoint so a
 * tab counter does not pull the full program listing with its per-program stats
 * and affiliate previews. Omitting the status counts every program of the brand.
 */
const fetchProgramsCount = async (
  filters: ProgramsCountFilters
): Promise<ProgramsCountResponse> => {
  const params = new URLSearchParams();
  if (filters.status) {
    params.append('status', filters.status);
  }

  const query = params.toString();
  const { data } = await api.get<ApiResponse<ProgramsCountResponse>>(
    `/dashboard/programs/count${query ? `?${query}` : ''}`
  );

  return data.data ?? { count: 0 };
};

export const useProgramsCountQuery = (filters: ProgramsCountFilters = {}) => {
  return useQuery<ProgramsCountResponse, Error>({
    queryKey: ['programs', 'count', filters],
    queryFn: () => fetchProgramsCount(filters),
    // Aligned with the program listing, so a tab count and its grid do not drift.
    staleTime: 5 * 60 * 1000,
  });
};
